
import { useState } from 'react';
import { Link } from 'react-router-dom';

interface DealsNewsletterProps {
  formUrl: string;
}

export default function DealsNewsletter({ formUrl }: DealsNewsletterProps) {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setStatus('loading');
    try {
      const formData = new URLSearchParams();
      formData.append('email', email);
      formData.append('source', 'deals-newsletter');

      const res = await fetch(formUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formData.toString(),
      });

      if (res.ok) {
        setStatus('success');
        setEmail('');
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <section className="py-20">
      <div className="max-w-7xl mx-auto px-6">
        <div className="relative rounded-2xl overflow-hidden bg-gradient-to-r from-teal-700 to-teal-900 p-12">
          <div className="absolute top-0 right-0 w-72 h-72 bg-teal-600/20 rounded-full -translate-y-1/2 translate-x-1/3"></div>
          <div className="absolute bottom-0 left-1/4 w-48 h-48 bg-amber-500/10 rounded-full translate-y-1/2"></div>

          <div className="relative flex flex-col lg:flex-row items-center justify-between gap-10">
            <div className="text-white max-w-lg">
              <div className="flex items-center gap-2 mb-3">
                <i className="ri-mail-send-line text-amber-400 text-xl"></i>
                <span className="text-amber-400 font-semibold text-sm uppercase tracking-wider">
                  Weekly Savings
                </span>
              </div>
              <h2 className="text-3xl font-bold mb-3">Never Miss a Deal</h2>
              <p className="text-white/80 text-sm leading-relaxed">
                Get the best Roseville deals and coupons delivered to your inbox every week. Be the first to know about new offers from local businesses.
              </p>
            </div>

            <div className="w-full max-w-md">
              {status === 'success' ? (
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center">
                  <div className="w-12 h-12 bg-amber-400 rounded-full flex items-center justify-center mx-auto mb-3">
                    <i className="ri-check-line text-white text-2xl"></i>
                  </div>
                  <p className="text-white font-semibold">You&apos;re subscribed!</p>
                  <p className="text-white/70 text-sm mt-1">Check your inbox for this week&apos;s deals.</p>
                </div>
              ) : (
                <form
                  id="deals-newsletter-form"
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="flex flex-col gap-3"
                >
                  <div className="flex items-center bg-white rounded-xl overflow-hidden">
                    <input
                      type="email"
                      name="email"
                      placeholder="Enter your email address"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="flex-1 px-4 py-3.5 text-sm text-gray-800 placeholder-gray-400 outline-none bg-transparent"
                    />
                    <button
                      type="submit"
                      disabled={status === 'loading'}
                      className="whitespace-nowrap px-6 py-3.5 bg-amber-500 text-white text-sm font-semibold hover:bg-amber-600 transition-colors cursor-pointer disabled:opacity-60"
                    >
                      {status === 'loading' ? 'Subscribing...' : 'Subscribe'}
                    </button>
                  </div>
                  {status === 'error' && (
                    <p className="text-red-300 text-xs">Something went wrong. Please try again.</p>
                  )}
                  <p className="text-white/50 text-xs">
                    No spam, ever. Unsubscribe anytime.
                  </p>
                </form>
              )}
            </div>
          </div>

          <div className="relative mt-10 pt-8 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-white/60 text-sm">
              Are you a local business? Get your deals featured here.
            </p>
            <Link
              to="/advertise"
              className="whitespace-nowrap px-5 py-2.5 bg-white/10 border border-white/20 text-white text-sm font-semibold rounded-lg hover:bg-white/20 transition-colors cursor-pointer"
            >
              Advertise With Us
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
